<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <div class="header">
        <h2>고객 위치 지도</h2>
        <span @click="close">
          <i class="ph ph-x"></i>
        </span>
      </div>
      
      <div class="map-wrapper">
        <KakaoMap
          :lat="37.566826"
          :lng="126.9786567"
          @onLoadKakaoMap="onLoadKakaoMap"
        >
          <KakaoMapMarker
            v-for="(marker, index) in markerList"
            :key="marker.id ?? index"
            :lat="marker.lat"
            :lng="marker.lng"
            :infoWindow="marker.infoWindow"
            :clickable="true"
            @onClickKakaoMapMarker="onClickMarker(marker)"
          />
        </KakaoMap>
        
        <div class="map-loading" v-if="isLoading">
          <i class="ph ph-spinner"></i>
          <span>고객 위치 불러오는 중...</span>
        </div>
      </div>
      
      <div class="map-info" v-if="markerList.length > 0">
        <i class="ph-fill ph-users"></i>
        <span>{{ markerList.length }}명의 고객 위치 (전체 {{ totalCustomers }}명)</span>
      </div>
      
      <div class="map-empty" v-else-if="!isLoading">
        <i class="ph ph-map-trifold"></i>
        <span>위치 정보가 있는 고객이 없습니다</span>
        <small>고객 상세 페이지에서 지도를 확인하면 위치가 자동 수집됩니다</small>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global kakao */
import { ref, inject, defineProps, defineEmits, defineExpose } from 'vue';
import { KakaoMap, KakaoMapMarker } from 'vue3-kakao-maps';

const sql_query = inject('sql_query');
const member_info = inject('member_info');

const props = defineProps({
  oth_showCustomerMap: {
    type: Function,
    default: () => {}
  }
});

const emit = defineEmits(['close', 'markerClick', 'loaded']);

const map = ref();
const markerList = ref([]);
const totalCustomers = ref(0);
const isLoading = ref(false);

const close = () => {
  props.oth_showCustomerMap(false);
  emit('close');
};

const onLoadKakaoMap = async (mapRef) => {
  map.value = mapRef;
  await loadCustomerMarkers();
};

const loadCustomerMarkers = async () => {
  isLoading.value = true;
  
  try {
    const result = await sql_query(
      `SELECT * FROM customers 
       WHERE mb_id = '${member_info.mb_id}' 
       AND lat IS NOT NULL AND lng IS NOT NULL
       ORDER BY name`
    );
    
    const totalResult = await sql_query(
      `SELECT COUNT(*) as cnt FROM customers WHERE mb_id = '${member_info.mb_id}'`
    );
    totalCustomers.value = totalResult[0]?.cnt || 0;
    
    const customers = result || [];
    
    if (customers.length === 0) {
      isLoading.value = false;
      emit('loaded', []);
      return;
    }
    
    const bounds = new kakao.maps.LatLngBounds();
    
    markerList.value = customers.map(customer => {
      bounds.extend(new kakao.maps.LatLng(Number(customer.lat), Number(customer.lng)));
      
      return {
        id: customer.customer_id,
        lat: Number(customer.lat),
        lng: Number(customer.lng),
        customer: customer,
        infoWindow: {
          content: `
            <div style="padding:12px 16px;min-width:180px;">
              <div style="font-size:15px;font-weight:600;margin-bottom:6px;color:#000;">${customer.name}</div>
              ${customer.phone ? `<div style="font-size:13px;color:#666;margin-bottom:4px;">${customer.phone}</div>` : ''}
              ${customer.address || customer.location ? `<div style="font-size:12px;color:#999;">${customer.address || customer.location}</div>` : ''}
            </div>
          `,
          visible: false
        }
      };
    });
    
    map.value?.setBounds(bounds);
    emit('loaded', customers);
  } catch (error) {
    console.error('고객 조회 실패:', error);
  }
  
  isLoading.value = false;
};

const onClickMarker = (marker) => {
  // 다른 마커의 인포윈도우 닫기
  markerList.value.forEach(m => {
    if (m.id !== marker.id && m.infoWindow) {
      m.infoWindow.visible = false;
    }
  });
  
  // 클릭한 마커의 인포윈도우 토글
  if (marker.infoWindow) {
    marker.infoWindow.visible = !marker.infoWindow.visible;
  }
  
  emit('markerClick', marker.customer);
};

const refresh = async () => {
  markerList.value = [];
  await loadCustomerMarkers();
};

defineExpose({ refresh, markerList, totalCustomers });
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 99;
  backdrop-filter: blur(2px);
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 90%;
  max-width: 800px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
  gap: 16px;
  display: flex;
  flex-direction: column;
}

.paper > .header {
  width: 100%;
  height: var(--font-size-3xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.paper > .header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
}

.paper > .header > span {
  width: var(--font-size-3xl);
  height: var(--font-size-3xl);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.paper > .header > span > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

.map-wrapper {
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.map-wrapper :deep(> div) {
  width: 100% !important;
  height: 100% !important;
}

.map-info {
  padding: 12px 16px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  flex-shrink: 0;
}

.map-info i {
  color: var(--color-brand);
  font-size: var(--font-size-lg);
}

.map-empty {
  padding: 24px;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.map-empty i {
  font-size: 32px;
  opacity: 0.5;
}

.map-empty small {
  font-size: var(--font-size-sm);
  opacity: 0.7;
}

.map-loading {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  z-index: 10;
}

.map-loading i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .dialog > .paper {
    width: 100%;
    height: 100%;
    border-radius: 0;
    border: none;
  }
}
</style>